import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import { ApiError, fetchWatchlists, type Watchlist, type WatchlistsResponse } from './api';

function watchlistsKey(token: string) {
  return ['watchlists', token] as const;
}

/** The caller's named watchlists, shared by the Watchlists page and ticker pages. */
export function useWatchlists(token: string) {
  return useQuery<WatchlistsResponse>({
    queryKey: watchlistsKey(token),
    queryFn: () => fetchWatchlists(token),
    enabled: Boolean(token),
  });
}

/**
 * Applies a mutation's result straight to the cached list so the UI updates
 * without a refetch; every watchlist write returns the list it changed.
 */
export function useWatchlistCache(token: string) {
  const queryClient = useQueryClient();
  const update = (change: (lists: Watchlist[]) => Watchlist[]) => {
    queryClient.setQueryData<WatchlistsResponse>(watchlistsKey(token), (data) =>
      data ? { ...data, watchlists: change(data.watchlists) } : data,
    );
  };
  return {
    added: (watchlist: Watchlist) => update((lists) => [...lists, watchlist]),
    replaced: (watchlist: Watchlist) =>
      update((lists) => lists.map((w) => (w.id === watchlist.id ? watchlist : w))),
    removed: (id: number) => update((lists) => lists.filter((w) => w.id !== id)),
  };
}

/** A user-facing sentence for a failed watchlist request. */
export function watchlistErrorMessage(error: unknown, symbol?: string): string {
  if (error instanceof ApiError) {
    switch (error.detail) {
      case 'duplicate_name':
        return 'You already have a watchlist with that name.';
      case 'watchlist_limit':
        return 'You have reached the maximum number of watchlists. Delete one to make room.';
      case 'ticker_limit':
        return 'This watchlist is full. Remove a ticker to add another.';
      case 'pro_required':
        return `${symbol ?? 'This ticker'} is available on the Pro plan.`;
      case 'unknown ticker':
        return `${symbol ? `“${symbol}”` : 'That ticker'} is not in the catalog.`;
      case 'unknown watchlist':
        return 'That watchlist no longer exists. Reload the page to refresh your lists.';
    }
    if (error.status === 401) return 'Your session has expired. Sign in again.';
    if (error.status === 422) return 'Watchlist names must be 1 to 60 characters.';
  }
  return 'Something went wrong. Please try again.';
}

/** Runs one watchlist request at a time and turns its failure into an inline message. */
export function useWatchlistAction() {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  async function run(action: () => Promise<void>, symbol?: string): Promise<boolean> {
    setBusy(true);
    setError(null);
    try {
      await action();
      return true;
    } catch (caught) {
      setError(watchlistErrorMessage(caught, symbol));
      return false;
    } finally {
      setBusy(false);
    }
  }
  return { busy, error, run };
}

export type WatchlistTicker = Watchlist['tickers'][number];
export type ScoredTicker = WatchlistTicker & { latest: NonNullable<WatchlistTicker['latest']> };
export type RankOrder = 'highest' | 'lowest';

export interface WatchlistSummary {
  /** Tickers with a latest score the caller may see, ranked by `order`. */
  ranked: ScoredTicker[];
  /** Tickers without a visible score: locked by plan, or never scored. */
  unranked: WatchlistTicker[];
  /**
   * Equal-weight mean of each scored ticker's latest daily score and class
   * probabilities. Null when nothing is scored: never a fabricated zero.
   */
  average: { score: number; positive: number; neutral: number; negative: number } | null;
  highest: ScoredTicker | null;
  lowest: ScoredTicker | null;
}

function mean(values: number[]): number {
  return values.reduce((sum, value) => sum + value, 0) / values.length;
}

export function summarizeWatchlist(
  watchlist: Watchlist,
  order: RankOrder = 'highest',
): WatchlistSummary {
  const scored: ScoredTicker[] = [];
  const unranked: WatchlistTicker[] = [];
  for (const ticker of watchlist.tickers) {
    if (!ticker.locked && ticker.latest) scored.push({ ...ticker, latest: ticker.latest });
    else unranked.push(ticker);
  }
  // Ties keep symbol order so the ranking is stable between renders.
  const byScore = [...scored].sort(
    (a, b) => b.latest.score - a.latest.score || a.symbol.localeCompare(b.symbol),
  );
  const average = scored.length
    ? {
        score: mean(scored.map((t) => t.latest.score)),
        positive: mean(scored.map((t) => t.latest.positive)),
        neutral: mean(scored.map((t) => t.latest.neutral)),
        negative: mean(scored.map((t) => t.latest.negative)),
      }
    : null;
  return {
    ranked: order === 'highest' ? byScore : [...byScore].reverse(),
    unranked,
    average,
    highest: byScore[0] ?? null,
    lowest: byScore.at(-1) ?? null,
  };
}
